import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import API from '../api';
import styles from './Work.module.css';
import navBarIcon from '../images/Nav Bar Icon.png';
import PageHeading from '../components/PageHeading';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProjectCard from '../components/ProjectCard';
import { useNavigate } from 'react-router-dom';
import MobileProjectCard from '../components/MobileProjectCard';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Work = () => {
  const [projects, setProjects] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [editMode, setEditMode] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newProject, setNewProject] = useState({ projectName: '', client: '', year: '', description: '' });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await API.get('/projects');
      console.log('Loaded projects:', response.data);
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
      setError('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateProject = async (file) => {
    if (!newProject.projectName || !newProject.client || !newProject.year) {
      toast.error('Project name, client, and year are required');
      return;
    }
    try {
      setCreating(true);
      const form = new FormData();
      form.append('projectName', newProject.projectName);
      form.append('client', newProject.client);
      form.append('year', newProject.year);
      if (newProject.description) form.append('description', newProject.description);
      if (file) form.append('image', file);
      const res = await API.post('/projects', form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setProjects((prev) => [res.data, ...prev]);
      setNewProject({ projectName: '', client: '', year: '', description: '' });
      toast.success('Project created');
    } catch (e) {
      toast.error('Failed to create project');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProject = async (id) => {
    if (!window.confirm('Delete this project?')) return;
    try {
      await API.delete(`/projects/${id}`);
      setProjects((prev) => prev.filter((p) => p._id !== id));
      toast.success('Project deleted');
    } catch (e) {
      toast.error('Failed to delete project');
    }
  };

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <main className={styles.workContainer}>
          <div className={styles.loadingState}>
            <div className={styles.spinner}></div>
            <p>Loading projects...</p>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.pageContainer}>
        <Header />
        <main className={styles.workContainer}>
          <div className={styles.errorState}>
            <p>⚠️ {error}</p>
            <button onClick={loadProjects}>Try Again</button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    // This wrapper controls the entire page layout
    <div className={styles.pageContainer}>
      {/* HEADER IS NOW A SIBLING TO MAIN */}
      <Header />

      {/* MAIN CONTENT AREA */}
      <main className={styles.workContainer}>
        <PageHeading title="Our Work" />
        {isAdmin && (
          <div style={{ width: '100%', maxWidth: 1200, margin: '10px auto', display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
            <button onClick={() => setEditMode((v) => !v)}>{editMode ? 'Done' : 'Edit'}</button>
          </div>
        )}
        {isAdmin && editMode && (
          <div style={{ width: '100%', maxWidth: 1200, margin: '10px auto', padding: 12, border: '1px solid rgba(255,255,255,0.2)', borderRadius: 8 }}>
            <h3 style={{ marginBottom: 8 }}>Add Project</h3>
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: '1fr 1fr 1fr' }}>
              <input placeholder="Project Name" value={newProject.projectName} onChange={(e) => setNewProject({ ...newProject, projectName: e.target.value })} />
              <input placeholder="Client" value={newProject.client} onChange={(e) => setNewProject({ ...newProject, client: e.target.value })} />
              <input placeholder="Year" value={newProject.year} onChange={(e) => setNewProject({ ...newProject, year: e.target.value })} />
            </div>
            <textarea placeholder="Description (optional)" value={newProject.description} onChange={(e) => setNewProject({ ...newProject, description: e.target.value })} rows={3} style={{ width: '100%', marginTop: 8 }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
              <label>
                <input type="file" accept="image/*" onChange={(e) => handleCreateProject(e.target.files?.[0])} disabled={creating} />
              </label>
              <button onClick={() => handleCreateProject()} disabled={creating}>{creating ? 'Creating...' : 'Create without image'}</button>
            </div>
          </div>
        )}
        
        {projects.length === 0 ? (
          <div className={styles.noProjects}>
            <p>No projects available at the moment.</p>
          </div>
        ) : (
          <>
            <div className={styles.cardsContainer}>
              {projects.map((project) => {
                const backendOrigin = String(API.defaults.baseURL || '')
                  .replace(/\/$/, '')
                  .replace(/\/api$/, '');
                const resolvedImageUrl = project.imageUrl && !/^https?:/i.test(project.imageUrl)
                  ? `${backendOrigin}${project.imageUrl}`
                  : project.imageUrl;
                return (
                  <div key={project._id} style={{ position: 'relative' }}>
                    {isAdmin && editMode && (
                      <button onClick={() => handleDeleteProject(project._id)} style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>Delete</button>
                    )}
                    <div onClick={() => navigate(`/work/${project._id}`)} style={{ cursor: 'pointer' }}>
                      <ProjectCard
                        imageUrl={resolvedImageUrl}
                        projectName={project.projectName}
                        client={project.client}
                        year={project.year}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <div className={styles.mobileCardsContainer}>
              {projects.map((project) => {
                const backendOrigin = String(API.defaults.baseURL || '')
                  .replace(/\/$/, '')
                  .replace(/\/api$/, '');
                const resolvedImageUrl = project.imageUrl && !/^https?:/i.test(project.imageUrl)
                  ? `${backendOrigin}${project.imageUrl}`
                  : project.imageUrl;
                return (
                  <div key={project._id} style={{ position: 'relative' }}>
                    {isAdmin && editMode && (
                      <button onClick={() => handleDeleteProject(project._id)} style={{ position: 'absolute', top: 8, right: 8, zIndex: 2 }}>Delete</button>
                    )}
                    <div onClick={() => navigate(`/work/${project._id}`)} style={{ cursor: 'pointer' }}>
                      <MobileProjectCard
                        imageUrl={resolvedImageUrl}
                        alt={project.projectName}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </main>
      
      {/* FOOTER */}
      <Footer />
    </div>
  );
};

export default Work; 